import React, { useState, useEffect, useMemo } from "react";
import style from "./CreateSubscriptionModalStyle";
import toast from "react-hot-toast";
import {
  getAllSubscriptionTypes,
  createBatchSubscriptions,
} from "../../../Services/subscriptionService";
import { getEnterprisesWithoutSubscription } from "../../../Services/enterpriseService";
import useDebounce from "../../../hooks/useDebounce";

export default function CreateSubscriptionModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("2");
  const [selectedEnterpriseIds, setSelectedEnterpriseIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const types = await getAllSubscriptionTypes();
        setSubscriptionTypes(types);
      } catch (error) {
        toast.error("Falha ao carregar os tipos de plano.");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (step === 2) {
      const fetchEnterprises = async () => {
        try {
          setLoading(true);
          const enterpriseData = await getEnterprisesWithoutSubscription(
            debouncedSearchTerm
          );
          setEnterprises(enterpriseData);
        } catch (error) {
          toast.error("Falha ao buscar empresas.");
        } finally {
          setLoading(false);
        }
      };
      fetchEnterprises();
    }
  }, [step, debouncedSearchTerm]);

  const handleNextStep = () => {
    if (!selectedTypeId) {
      toast.error("Selecione um tipo de plano.");
      return;
    }
    setStep(2);
  };

  const handleEnterpriseToggle = (id) => {
    setSelectedEnterpriseIds((prev) =>
      prev.includes(id) ? prev.filter((eId) => eId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedEnterpriseIds.length === enterprises.length) {
      setSelectedEnterpriseIds([]);
    } else {
      setSelectedEnterpriseIds(enterprises.map((e) => e.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedEnterpriseIds.length === 0) {
      toast.error("Selecione pelo menos uma empresa.");
      return;
    }
    setIsCreating(true);
    try {
      const data = {
        subscriptionTypeId: parseInt(selectedTypeId, 10),
        status: parseInt(selectedStatus, 10),
        enterpriseIds: selectedEnterpriseIds,
      };
      await createBatchSubscriptions(data);
      toast.success("Assinaturas criadas com sucesso!");
      onSuccess();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Falha ao criar assinaturas."
      );
    } finally {
      setIsCreating(false);
    }
  };

  const selectedPlan = useMemo(
    () => subscriptionTypes.find((t) => t.id === parseInt(selectedTypeId, 10)),
    [selectedTypeId, subscriptionTypes]
  );

  return (
    <div style={style.overlay} onClick={onClose}>
      <div style={style.modal} onClick={(e) => e.stopPropagation()}>
        <header style={style.header}>
          <h2 style={style.title}>Criar Nova Assinatura</h2>
          <button style={style.closeButton} onClick={onClose}>
            &times;
          </button>
        </header>

        <div style={style.content}>
          {step === 1 && (
            <div>
              <div style={style.formGroup}>
                <label style={style.label}>1. Selecione o Plano</label>
                <select
                  style={style.select}
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                >
                  <option value="">Escolha um plano...</option>
                  {subscriptionTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={style.formGroup}>
                <label style={style.label}>2. Defina o Status Inicial</label>
                <div style={style.radioGroup}>
                  <label style={style.radioLabel}>
                    <input
                      type="radio"
                      name="status"
                      value="2"
                      checked={selectedStatus === "2"}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    />{" "}
                    Pendente
                  </label>
                  <label style={style.radioLabel}>
                    <input
                      type="radio"
                      name="status"
                      value="1"
                      checked={selectedStatus === "1"}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    />{" "}
                    Ativo
                  </label>
                  <label style={style.radioLabel}>
                    <input
                      type="radio"
                      name="status"
                      value="3"
                      checked={selectedStatus === "3"}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    />{" "}
                    Cancelado
                  </label>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p style={style.summaryText}>
                Plano: <strong>{selectedPlan?.name}</strong> | Status:{" "}
                <strong>
                  {
                    { 1: "Ativo", 2: "Pendente", 3: "Cancelado" }[
                      selectedStatus
                    ]
                  }
                </strong>
              </p>
              <div style={style.formGroup}>
                <label style={style.label}>3. Selecione as Empresas</label>
                <input
                  type="text"
                  style={style.searchInput}
                  placeholder="Buscar por nome ou CNPJ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={style.enterpriseList}>
                {loading ? (
                  <p>Buscando empresas...</p>
                ) : (
                  <>
                    <div style={style.listHeader}>
                      <label style={style.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={
                            selectedEnterpriseIds.length > 0 &&
                            selectedEnterpriseIds.length === enterprises.length
                          }
                          onChange={handleSelectAll}
                        />
                        Selecionar Todos ({selectedEnterpriseIds.length})
                      </label>
                    </div>
                    {enterprises.length > 0 ? (
                      enterprises.map((enterprise) => (
                        <div key={enterprise.id} style={style.enterpriseItem}>
                          <label style={style.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={selectedEnterpriseIds.includes(
                                enterprise.id
                              )}
                              onChange={() =>
                                handleEnterpriseToggle(enterprise.id)
                              }
                            />
                            <div>
                              <span style={style.enterpriseName}>
                                {enterprise.name}
                              </span>
                              <span style={style.enterpriseCnpj}>
                                {enterprise.cnpj}
                              </span>
                            </div>
                          </label>
                        </div>
                      ))
                    ) : (
                      <p style={style.noResults}>Nenhuma empresa encontrada.</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <footer style={style.footer}>
          {step === 1 && (
            <button
              style={style.buttonPrimary}
              onClick={handleNextStep}
              disabled={loading}
            >
              Avançar
            </button>
          )}
          {step === 2 && (
            <>
              <button style={style.buttonSecondary} onClick={() => setStep(1)}>
                Voltar
              </button>
              <button
                style={style.buttonPrimary}
                onClick={handleSubmit}
                disabled={isCreating}
              >
                {isCreating
                  ? "Criando..."
                  : `Criar ${selectedEnterpriseIds.length} Assinatura(s)`}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
